/**
 * React Hook for LLM-Assisted Rule Suggestions (Epic #568 Phase 6)
 *
 * Provides access to AI-generated policy rule suggestions with review workflow:
 * - Generate suggestions via LLM (Claude via Bedrock)
 * - Review pending suggestions
 * - Accept/reject suggestions
 * - Get detailed explanations
 */

import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export enum SuggestionStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum SuggestionCategory {
  APPROVAL = 'APPROVAL',
  ESCALATION = 'ESCALATION',
  VALIDATION = 'VALIDATION',
  NOTIFICATION = 'NOTIFICATION',
  ROUTING = 'ROUTING',
  COMPLIANCE = 'COMPLIANCE',
  PERFORMANCE = 'PERFORMANCE',
}

export interface SuggestedCondition {
  field: string;
  operator: string;
  value: string;
}

export interface SuggestedAction {
  actionType: string;
  params: string; // JSON string
}

export interface SuggestedRuleDefinition {
  name: string;
  description?: string;
  conditions: SuggestedCondition[];
  actions: SuggestedAction[];
  priority?: number;
  terminal?: boolean;
}

export interface SuggestedRule {
  suggestionId: string;
  tenantId: string;
  ruleName: string;
  description: string;
  rule: SuggestedRuleDefinition;
  confidence: number;
  reasoning: string;
  category: SuggestionCategory;
  status: SuggestionStatus;
  createdAt: string;
  generatedBy: string;
  relatedRuleIds: string[];
  estimatedImpact: number;
}

export interface SuggestionContextInput {
  tenantId: string;
  focusAreas?: SuggestionCategory[];
  maxSuggestions?: number;
  minConfidence?: number;
}

export interface SuggestionResult {
  suggestions: SuggestedRule[];
  count: number;
  provider: string;
}

export interface SuggestionExplanation {
  suggestionId: string;
  explanation: string;
}

// ============================================================================
// GRAPHQL QUERIES & MUTATIONS
// ============================================================================

const SUGGEST_RULES = gql`
  query SuggestRules($input: GqlSuggestionContextInput!) {
    suggestRules(input: $input) {
      suggestions {
        suggestionId
        tenantId
        ruleName
        description
        rule {
          name
          description
          conditions {
            field
            operator
            value
          }
          actions {
            actionType
            params
          }
          priority
          terminal
        }
        confidence
        reasoning
        category
        status
        createdAt
        generatedBy
        relatedRuleIds
        estimatedImpact
      }
      count
      provider
    }
  }
`;

const LIST_PENDING_SUGGESTIONS = gql`
  query ListPendingSuggestions($tenantId: String!) {
    listPendingSuggestions(tenantId: $tenantId) {
      suggestionId
      tenantId
      ruleName
      description
      rule {
        name
        description
        conditions {
          field
          operator
          value
        }
        actions {
          actionType
          params
        }
        priority
        terminal
      }
      confidence
      reasoning
      category
      status
      createdAt
      generatedBy
      relatedRuleIds
      estimatedImpact
    }
  }
`;

const GET_SUGGESTION = gql`
  query GetSuggestion($suggestionId: String!) {
    getSuggestion(suggestionId: $suggestionId) {
      suggestionId
      tenantId
      ruleName
      description
      rule {
        name
        description
        conditions {
          field
          operator
          value
        }
        actions {
          actionType
          params
        }
        priority
        terminal
      }
      confidence
      reasoning
      category
      status
      createdAt
      generatedBy
      relatedRuleIds
      estimatedImpact
    }
  }
`;

const EXPLAIN_SUGGESTION = gql`
  query ExplainSuggestion($suggestionId: String!) {
    explainSuggestion(suggestionId: $suggestionId) {
      suggestionId
      explanation
    }
  }
`;

const ACCEPT_SUGGESTION = gql`
  mutation AcceptSuggestion($input: GqlSuggestionActionInput!) {
    acceptSuggestion(input: $input) {
      suggestionId
      status
    }
  }
`;

const REJECT_SUGGESTION = gql`
  mutation RejectSuggestion($input: GqlSuggestionActionInput!) {
    rejectSuggestion(input: $input) {
      suggestionId
      status
    }
  }
`;

const CLEAR_SUGGESTIONS = gql`
  mutation ClearSuggestions($tenantId: String!) {
    clearSuggestions(tenantId: $tenantId)
  }
`;

const IS_SERVICE_AVAILABLE = gql`
  query IsSuggestionServiceAvailable {
    isSuggestionServiceAvailable
  }
`;

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Check if the suggestion service is available
 */
export function useSuggestionServiceAvailable() {
  const { data, loading, error } = useQuery<Record<string, any>>(IS_SERVICE_AVAILABLE);

  return {
    isAvailable: data?.isSuggestionServiceAvailable ?? false,
    loading,
    error,
  };
}

/**
 * Generate rule suggestions using LLM
 *
 * @example
 * ```tsx
 * const { generateSuggestions, suggestions, loading } = useGenerateSuggestions();
 *
 * const handleGenerate = async () => {
 *   await generateSuggestions({
 *     tenantId: 'tenant-123',
 *     focusAreas: [SuggestionCategory.APPROVAL, SuggestionCategory.COMPLIANCE],
 *     maxSuggestions: 5,
 *     minConfidence: 0.7,
 *   });
 * };
 * ```
 */
export function useGenerateSuggestions() {
  const [fetchSuggestions, { data, loading, error }] = useLazyQuery<Record<string, any>>(SUGGEST_RULES, {
    fetchPolicy: 'network-only', // Always fetch fresh suggestions
  });

  const generateSuggestions = useCallback(
    async (input: SuggestionContextInput) => {
      const result = await fetchSuggestions({
        variables: { input },
      });
      return result.data?.suggestRules as SuggestionResult | undefined;
    },
    [fetchSuggestions],
  );

  const result = data?.suggestRules as SuggestionResult | undefined;

  return {
    generateSuggestions,
    suggestions: result?.suggestions ?? [],
    count: result?.count ?? 0,
    provider: result?.provider ?? 'unknown',
    loading,
    error,
  };
}

/**
 * Get pending suggestions for a tenant
 *
 * @example
 * ```tsx
 * const { suggestions, loading, refetch } = usePendingSuggestions('tenant-123');
 * ```
 */
export function usePendingSuggestions(tenantId: string) {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(LIST_PENDING_SUGGESTIONS, {
    variables: { tenantId },
    skip: !tenantId,
  });

  const suggestions = useMemo(
    () => (data?.listPendingSuggestions ?? []) as SuggestedRule[],
    [data],
  );

  // Group by category for easier display
  const byCategory = useMemo(() => {
    const grouped: Record<SuggestionCategory, SuggestedRule[]> = {
      [SuggestionCategory.APPROVAL]: [],
      [SuggestionCategory.ESCALATION]: [],
      [SuggestionCategory.VALIDATION]: [],
      [SuggestionCategory.NOTIFICATION]: [],
      [SuggestionCategory.ROUTING]: [],
      [SuggestionCategory.COMPLIANCE]: [],
      [SuggestionCategory.PERFORMANCE]: [],
    };

    suggestions.forEach((s) => {
      if (grouped[s.category]) {
        grouped[s.category].push(s);
      }
    });

    return grouped;
  }, [suggestions]);

  // Sort by confidence (highest first)
  const sortedByConfidence = useMemo(
    () => [...suggestions].sort((a, b) => b.confidence - a.confidence),
    [suggestions],
  );

  return {
    suggestions,
    byCategory,
    sortedByConfidence,
    count: suggestions.length,
    loading,
    error,
    refetch,
  };
}

/**
 * Get a single suggestion by ID
 */
export function useSuggestion(suggestionId: string) {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_SUGGESTION, {
    variables: { suggestionId },
    skip: !suggestionId,
  });

  return {
    suggestion: data?.getSuggestion as SuggestedRule | undefined,
    loading,
    error,
  };
}

/**
 * Get detailed explanation for a suggestion
 */
export function useSuggestionExplanation(suggestionId: string) {
  const [fetchExplanation, { data, loading, error }] = useLazyQuery<Record<string, any>>(EXPLAIN_SUGGESTION);

  const getExplanation = useCallback(
    async (id: string) => {
      const result = await fetchExplanation({
        variables: { suggestionId: id },
      });
      return result.data?.explainSuggestion as SuggestionExplanation | undefined;
    },
    [fetchExplanation],
  );

  return {
    getExplanation,
    explanation: data?.explainSuggestion as SuggestionExplanation | undefined,
    loading,
    error,
  };
}

/**
 * Accept or reject suggestions
 *
 * @example
 * ```tsx
 * const { acceptSuggestion, rejectSuggestion, loading } = useSuggestionActions();
 *
 * // Accept a suggestion
 * await acceptSuggestion('tenant-123', 'suggestion-456');
 *
 * // Reject a suggestion
 * await rejectSuggestion('tenant-123', 'suggestion-789');
 * ```
 */
export function useSuggestionActions() {
  const [acceptMutation, { loading: acceptLoading, error: acceptError }] =
    useMutation<Record<string, any>>(ACCEPT_SUGGESTION);
  const [rejectMutation, { loading: rejectLoading, error: rejectError }] =
    useMutation<Record<string, any>>(REJECT_SUGGESTION);
  const [clearMutation, { loading: clearLoading, error: clearError }] =
    useMutation<Record<string, any>>(CLEAR_SUGGESTIONS);

  const acceptSuggestion = useCallback(
    async (tenantId: string, suggestionId: string) => {
      const result = await acceptMutation({
        variables: {
          input: { tenantId, suggestionId },
        },
      });
      return result.data?.acceptSuggestion as SuggestedRule | undefined;
    },
    [acceptMutation],
  );

  const rejectSuggestion = useCallback(
    async (tenantId: string, suggestionId: string) => {
      const result = await rejectMutation({
        variables: {
          input: { tenantId, suggestionId },
        },
      });
      return result.data?.rejectSuggestion as SuggestedRule | undefined;
    },
    [rejectMutation],
  );

  const clearSuggestions = useCallback(
    async (tenantId: string) => {
      const result = await clearMutation({
        variables: { tenantId },
      });
      return result.data?.clearSuggestions as number | undefined;
    },
    [clearMutation],
  );

  return {
    acceptSuggestion,
    rejectSuggestion,
    clearSuggestions,
    loading: acceptLoading || rejectLoading || clearLoading,
    error: acceptError || rejectError || clearError,
  };
}

/**
 * Combined hook for suggestion review workflow
 *
 * @example
 * ```tsx
 * function SuggestionReviewer({ tenantId }: { tenantId: string }) {
 *   const {
 *     suggestions,
 *     generateNew,
 *     accept,
 *     reject,
 *     getExplanation,
 *     loading,
 *   } = useSuggestionReview(tenantId);
 *
 *   return (
 *     <div>
 *       <Button onClick={() => generateNew({ tenantId, maxSuggestions: 5 })}>
 *         Generate Suggestions
 *       </Button>
 *       {suggestions.map(s => (
 *         <SuggestionCard
 *           key={s.suggestionId}
 *           suggestion={s}
 *           onAccept={() => accept(s.suggestionId)}
 *           onReject={() => reject(s.suggestionId)}
 *           onExplain={() => getExplanation(s.suggestionId)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSuggestionReview(tenantId: string) {
  const { suggestions, byCategory, sortedByConfidence, count, loading: listLoading, refetch } =
    usePendingSuggestions(tenantId);
  const { generateSuggestions, loading: generateLoading } = useGenerateSuggestions();
  const { acceptSuggestion, rejectSuggestion, clearSuggestions, loading: actionLoading } =
    useSuggestionActions();
  const { getExplanation, loading: explanationLoading } = useSuggestionExplanation('');

  // Convenience wrappers that auto-include tenantId
  const accept = useCallback(
    async (suggestionId: string) => {
      const result = await acceptSuggestion(tenantId, suggestionId);
      await refetch();
      return result;
    },
    [tenantId, acceptSuggestion, refetch],
  );

  const reject = useCallback(
    async (suggestionId: string) => {
      const result = await rejectSuggestion(tenantId, suggestionId);
      await refetch();
      return result;
    },
    [tenantId, rejectSuggestion, refetch],
  );

  const clear = useCallback(async () => {
    const result = await clearSuggestions(tenantId);
    await refetch();
    return result;
  }, [tenantId, clearSuggestions, refetch]);

  const generateNew = useCallback(
    async (input: Omit<SuggestionContextInput, 'tenantId'>) => {
      const result = await generateSuggestions({ ...input, tenantId });
      await refetch();
      return result;
    },
    [tenantId, generateSuggestions, refetch],
  );

  return {
    // Data
    suggestions,
    byCategory,
    sortedByConfidence,
    count,

    // Actions
    generateNew,
    accept,
    reject,
    clear,
    getExplanation,
    refetch,

    // Loading states
    loading: listLoading || generateLoading || actionLoading || explanationLoading,
    isGenerating: generateLoading,
    isActing: actionLoading,
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get confidence level badge color
 */
export function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.8) return 'teal';
  if (confidence >= 0.7) return 'yellow';
  if (confidence >= 0.5) return 'orange';
  return 'red';
}

/**
 * Get category icon name for display
 */
export function getCategoryIcon(category: SuggestionCategory): string {
  switch (category) {
    case SuggestionCategory.APPROVAL:
      return 'check';
    case SuggestionCategory.ESCALATION:
      return 'arrow-up';
    case SuggestionCategory.VALIDATION:
      return 'shield-check';
    case SuggestionCategory.NOTIFICATION:
      return 'bell';
    case SuggestionCategory.ROUTING:
      return 'route';
    case SuggestionCategory.COMPLIANCE:
      return 'certificate';
    case SuggestionCategory.PERFORMANCE:
      return 'chart-line';
    default:
      return 'puzzle-piece';
  }
}

/**
 * Format impact percentage
 */
export function formatImpact(impact: number): string {
  const percentage = Math.round(impact * 100);
  return `${percentage > 0 ? '+' : ''}${percentage}%`;
}
